<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useSessionStore, type Session } from '@/domain/session/store'
import { fmtSessionTime } from '@/shared/format'
import StartDiscussionDialog from './StartDiscussionDialog.vue'

/* searchable=false：由外部（左栏全局搜索）统一驱动关键词，本组件不再显示自己的搜索框 */
withDefaults(defineProps<{ searchable?: boolean }>(), { searchable: true })
const STALE_MS = 3 * 24 * 60 * 60 * 1000
const emit = defineEmits<{ picked: [] }>()
const sess = useSessionStore()
const kw = ref('')
const error = ref('')
const menuFor = ref('')
const menuPos = ref({ left: 0, top: 0 })
const renaming = ref('')
const draft = ref('')
const discussFor = ref('')
const router = useRouter()
let timer: ReturnType<typeof setTimeout> | undefined

/* 状态色：3 天无活动=待归档（灰）；系统阻断=红；需再处理=黄；正常=绿 */
function dotState(s: Session) {
  if (Date.now() - s.updatedAt >= STALE_MS) return 'stale'
  return s.health && s.health !== 'ok' ? s.health : 'ok'
}
function timeText(s: Session) {
  const t = fmtSessionTime(s.updatedAt)
  return dotState(s) === 'stale' ? `待归档 · ${t}` : t
}

watch(kw, value => {
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => { void sess.searchSessions(value).catch(fail) }, 300)
})
onBeforeUnmount(() => { if (timer) clearTimeout(timer) })

const menuSession = computed(() => sess.sessions.find(s => s.id === menuFor.value))

function fail(e: unknown) { error.value = e instanceof Error ? e.message : '会话操作未完成' }
function open(id: string) {
  menuFor.value = ''
  void sess.openSession(id)
  emit('picked')
}
/* 悬停时标题超长则横向滚动展示全文；长度不够则不动 */
function hoverLabel(e: MouseEvent, on: boolean) {
  if (menuFor.value || renaming.value) return
  const label = (e.currentTarget as HTMLElement).querySelector<HTMLElement>('.label')
  if (!label) return
  if (!on) {
    label.classList.remove('scroll')
    return
  }
  const shift = label.scrollWidth - label.clientWidth
  if (shift <= 0) return
  label.style.setProperty('--shift', `-${shift}px`)
  label.style.setProperty('--dur', `${Math.min(9, Math.max(2, shift / 40))}s`)
  label.classList.add('scroll')
}

/* 更多菜单：固定在按钮下方，向右对齐并夹在视口内，避免被滚动容器裁切 */
function openMenu(e: MouseEvent, id: string) {
  e.stopPropagation()
  renaming.value = ''
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  menuFor.value = id
  menuPos.value = {
    left: Math.max(8, Math.min(rect.right - 136, window.innerWidth - 144)),
    top: rect.bottom + 4,
  }
}
async function doPin(id: string) {
  const pinned = !!sess.sessions.find(s => s.id === id)?.pinned
  menuFor.value = ''
  try { await sess.togglePin(id, !pinned); error.value = '' }
  catch (e) { fail(e) }
}
async function doArchive(id: string) {
  menuFor.value = ''
  try { await sess.archiveSession(id); error.value = '' }
  catch (e) { fail(e) }
}
function beginRename(id: string) {
  const target = sess.sessions.find(s => s.id === id)
  if (!target) return
  menuFor.value = ''
  renaming.value = id
  draft.value = target.title
  error.value = ''
}
/* 发起讨论：必须先持久化会话（persisted），后台要读取会话内容生成话题背景 */
function beginDiscuss(id: string) {
  if (!sess.sessions.find(s => s.id === id)?.persisted) return
  menuFor.value = ''
  discussFor.value = id
  error.value = ''
}
function onDiscussCreated(id: string) {
  discussFor.value = ''
  void router.push({ path: '/chat', query: { discussion: id } })
}
async function commitRename(id: string) {
  try { await sess.renameSession(id, draft.value); renaming.value = ''; error.value = '' }
  catch (e) { fail(e) }
}
</script>

<template>
  <div class="sessions">
    <input v-if="searchable" v-model="kw" class="find" type="search" maxlength="40" placeholder="搜索会话" aria-label="搜索会话">
    <p v-if="sess.sessionsError" role="alert">{{ sess.sessionsError }} <button type="button" @click="sess.loadSessions(true).catch(fail)">重试</button></p>
    <p v-else-if="sess.sessionsLoading && !sess.visibleSessions.length" role="status">正在读取会话…</p>
    <div v-else-if="!sess.visibleSessions.length" class="empty">还没有会话</div>
    <div v-for="s in sess.visibleSessions" :key="s.id" class="row" :class="{ on: s.id === sess.currentId, pinned: s.pinned }"
         @contextmenu.prevent="openMenu($event, s.id)">
      <template v-if="renaming === s.id">
        <input v-model="draft" class="rename" maxlength="40" aria-label="会话标题"
               @keydown.enter.prevent="commitRename(s.id)" @keydown.esc.prevent="renaming = ''">
        <button type="button" class="act" @click="commitRename(s.id)">保存</button>
        <button type="button" class="act" @click="renaming = ''">取消</button>
      </template>
      <template v-else>
        <button type="button" class="title" :title="s.title" @click="open(s.id)"
                @mouseenter="hoverLabel($event, true)" @mouseleave="hoverLabel($event, false)">
          <span class="dot" :data-health="dotState(s)" :title="dotState(s) === 'blocked' ? '系统异常，未完成' : dotState(s) === 'attention' ? '需要再处理' : dotState(s) === 'stale' ? '超过 3 天无活动，待归档' : '正常'" />
          <span class="label"><span class="label-text">{{ s.title }}</span></span>
          <span v-if="s.pinned" class="pin" title="已置顶"><svg viewBox="0 0 16 16" width="11" height="11"><path d="M9.4 1.8 14.2 6.6l-1.6 1.6-1.2-.4-2.6 2.6.3 1.9-1.7 1.7-2.3-2.3L2.4 14l-1.4-1.4 2.3-2.7L1 7.6l1.7-1.7 1.9.3 2.6-2.6-.4-1.2L8.4 1z" fill="currentColor"/></svg></span>
          <span class="time">{{ timeText(s) }}</span>
        </button>
        <button type="button" class="dots" title="更多操作" aria-haspopup="menu" @click="openMenu($event, s.id)">⋯</button>
      </template>
    </div>
    <button v-if="sess.sessionHasMore" type="button" class="more" :disabled="sess.sessionsLoading" @click="sess.loadSessions(false).catch(fail)">更早的会话</button>
    <p v-if="error" role="alert">{{ error }}</p>

    <Teleport to="body">
      <div v-if="menuFor" class="menu-mask" @click="menuFor = ''" @contextmenu.prevent="menuFor = ''" />
      <div v-if="menuFor" class="sess-menu" role="menu" :style="{ left: menuPos.left + 'px', top: menuPos.top + 'px' }">
        <button type="button" role="menuitem" @click="doPin(menuFor)">{{ menuSession?.pinned ? '取消置顶' : '置顶' }}</button>
        <button type="button" role="menuitem" :disabled="!menuSession?.persisted" @click="beginRename(menuFor)">重命名</button>
        <button type="button" role="menuitem" :disabled="!menuSession?.persisted" @click="beginDiscuss(menuFor)">讨论</button>
        <button type="button" role="menuitem" :disabled="!menuSession?.persisted" @click="doArchive(menuFor)">归档</button>
      </div>
      <StartDiscussionDialog v-if="discussFor" :session-id="discussFor" :session-title="sess.sessions.find(s => s.id === discussFor)?.title || ''" @close="discussFor = ''" @created="onDiscussCreated" />
    </Teleport>
  </div>
</template>

<style scoped>
.sessions { display: flex; flex-direction: column; gap: 4px; min-height: 0; }
.find, .rename { width: 100%; box-sizing: border-box; border: 1px solid var(--line); border-radius: 8px; background: var(--surface); color: var(--ink); font: inherit; font-size: var(--fs-sm); padding: 6px 8px; }
/* 搜索框居中并留出两侧空间 */
.find { width: calc(100% - 20px); margin: 0 auto 2px; }
.rename { flex: 1; min-width: 0; }
.row { position: relative; display: flex; align-items: center; gap: 2px; border-radius: 10px; transition: background .16s ease, box-shadow .16s ease; }
/* 悬停 / 当前会话：白底卡片浮在列表之上 */
.row:hover { background: var(--surface); box-shadow: var(--sh-1); }
.row.on { background: var(--surface); box-shadow: var(--sh-2); }
.title { flex: 1; min-width: 0; display: flex; align-items: center; gap: 8px; border: 0; background: transparent; color: var(--ink-2); text-align: left; font: inherit; font-size: var(--fs-base); padding: 8px 10px; cursor: pointer; }
.label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* 悬停滚动：只移动文字本身，裁剪框固定不动，避免文字滚到圆点左侧 */
.label-text { display: inline-block; white-space: nowrap; }
.label.scroll { text-overflow: clip; }
.label.scroll .label-text { animation: label-scroll var(--dur, 4s) ease-in-out infinite alternate; }
@keyframes label-scroll {
  0%, 18% { transform: translateX(0); }
  82%, 100% { transform: translateX(var(--shift, 0)); }
}
@media (prefers-reduced-motion: reduce) { .label.scroll .label-text { animation: none; } .label.scroll { text-overflow: ellipsis; } }
/* 时间标签在行内贴右；悬停时让位给叠在其上的更多按钮 */
.time { flex: none; font-size: var(--fs-2xs); color: var(--ink-4); font-variant-numeric: tabular-nums; }
.pin { flex: none; display: inline-flex; color: var(--accent); }
.row.on .title { color: var(--accent); font-weight: var(--fw-semibold); }
.row.pinned .label { font-weight: var(--fw-medium); }
.dot { width: 7px; height: 7px; border-radius: 50%; background: var(--teal); flex: none; }
/* 会话状态色：绿正常 / 红系统阻断 / 黄需再处理 / 灰待归档 */
.dot[data-health=blocked] { background: var(--danger, #b42318); }
.dot[data-health=attention] { background: var(--warn); }
.dot[data-health=stale] { background: var(--ink-4); }
/* 更多按钮：悬停才出现，出现时带浅灰底块便于发现；出现时让位给时间标签 */
.dots { position: absolute; right: 4px; top: 50%; transform: translateY(-50%); width: 24px; height: 24px; border: 1px solid transparent; border-radius: 6px; background: transparent; color: var(--ink-4); cursor: pointer; font: inherit; font-size: 14px; line-height: 1; opacity: 0; transition: opacity .14s ease, background .14s ease, border-color .14s ease; }
.row:hover .dots, .row:focus-within .dots { opacity: 1; background: var(--hover); border-color: var(--line-soft); color: var(--ink-2); box-shadow: var(--sh-1); }
.dots:hover { background: var(--surface-3); color: var(--ink); }
.row:hover .time { display: none; }
.act { flex: none; border: 0; background: transparent; color: var(--ink-4); cursor: pointer; font: inherit; font-size: var(--fs-2xs); padding: 4px; }
.act:hover { color: var(--accent); }
.more { border: 0; background: transparent; color: var(--ink-4); cursor: pointer; font: inherit; font-size: var(--fs-2xs); align-self: center; padding: 6px; }
.more:hover { color: var(--accent); }
.empty, [role=alert], [role=status] { margin: 0; padding: 6px; font-size: var(--fs-sm); color: var(--ink-4); text-align: center; line-height: 1.6; }
[role=alert] { color: var(--danger, #b42318); }
[role=alert] button { border: 0; background: transparent; color: inherit; cursor: pointer; font: inherit; text-decoration: underline; }
.menu-mask { position: fixed; inset: 0; z-index: 2000; }
.sess-menu { position: fixed; z-index: 2001; width: 136px; display: flex; flex-direction: column; padding: 4px; background: var(--surface); border: 1px solid var(--line-soft); border-radius: 10px; box-shadow: var(--sh-3, var(--sh-2)); }
.sess-menu button { border: 0; background: transparent; color: var(--ink-2); font: inherit; font-size: var(--fs-sm); text-align: left; padding: 8px 10px; border-radius: 7px; cursor: pointer; }
.sess-menu button:hover { background: var(--hover); color: var(--ink); }
.sess-menu button:disabled { color: var(--ink-4); cursor: not-allowed; background: transparent; }
</style>
